import React, { useState } from 'react';
import { ApiError } from '../../api/client';
import type { CreateRoleInput } from '../../features/roles/types';

export type AddRoleFormData = CreateRoleInput;

interface AddRoleModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: AddRoleFormData) => Promise<void>;
}

const AddRoleModal: React.FC<AddRoleModalProps> = ({ show, onHide, onSubmit }) => {
  const [formData, setFormData] = useState<AddRoleFormData>({ name: '', displayName: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.displayName.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      await onSubmit({
        name: formData.name.trim().toUpperCase(),
        displayName: formData.displayName.trim(),
      });
      setFormData({ name: '', displayName: '' });
      onHide();
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError && err.status === 409) {
        setError('A role with this name already exists.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create role');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-plus-circle me-2" />
              Add Role
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide} disabled={isLoading} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle me-2" />
                  {error}
                </div>
              )}
              <div className="mb-3">
                <label htmlFor="addRoleName" className="form-label">System Name (UPPER_SNAKE_CASE)</label>
                <input
                  type="text"
                  className="form-control"
                  id="addRoleName"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. COMMITTEE_MEMBER"
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="addRoleDisplayName" className="form-label">Display Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="addRoleDisplayName"
                  value={formData.displayName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, displayName: e.target.value }))}
                  placeholder="e.g. Committee Member"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-2" />
                    Add Role
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddRoleModal;
